import algoliasearch from 'algoliasearch/lite';
import { buildBookingAutoIcon, initializeGlider, removeMarkerHighlight, buildPopupHTML, buildMobilePopup, mapTilesStyle } from '../frontv2/map-utils';
import { getMapRadius, groupBy } from '../utils';

class MapWidget{
  constructor(){
    this.wrapper = document.getElementById('map');
    if(this.wrapper && typeof maplibregl !== "undefined"){
      this.markersPlaced = [];
      this.currencyInfos = {
        locale: document.body.dataset.currencyLocale.replace("_", "-"),
        rate: document.body.dataset.currencyRate,
        iso: document.body.dataset.currencyIso
      };
      this.mobileCardsContainer = this.wrapper.querySelector('#mobilePopupsContainer');
      this.utmParameters = `?utm_source=${this.wrapper.dataset.source}&utm_medium=${this.wrapper.dataset.medium}&utm_campaign=${this.wrapper.dataset.campaign}`;

      //check if it's a bot, if so do not load the map
      var botPattern = "(googlebot\/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)";
      var re = new RegExp(botPattern, 'i');
      var userAgent = navigator.userAgent;
      if(!re.test(userAgent)) {
        this.initMap();
      }
    }
  }

  initMap(){
    this.map = new maplibregl.Map({
      name: 'Winalist',
      container: 'map',
      style: mapTilesStyle,
      zoom: this.wrapper.dataset.zoom ?? 8,
      center: [this.wrapper.dataset.lng ?? '4.0333', this.wrapper.dataset.lat ?? '49.25'] //default to Champagne lat/lng
    });
    // this.map.addControl(new maplibregl.FullscreenControl());
    this.map.addControl(new maplibregl.NavigationControl({
      showCompass: false
    }));
    this.map.scrollZoom.disable();

    this.algolia = this.getAlgoliaClient();

    this.map.on('click', () => {
      removeMarkerHighlight();
    });

    this.performSearch();
  };

  getAlgoliaClient(){
    const env = document.body.dataset.env;
    const lang = document.body.dataset.lang.substr(0, 2);

    let prefix = '';
    if(env === 'localhost' || env == 'staging'){
      prefix = 'localhost_';
    }
    let experiences_index = prefix + 'experiences_' + lang;

    const client = algoliasearch('AK7I6BLE3J', '6b23703cf5bc843f0015aa31634bdeef');
    return client.initIndex(experiences_index);
  }

  //Perform the search through algolia
  performSearch(){
    let params = {
      query: '',
      hitsPerPage: 250,
      attributesToRetrieve: '*',
      responseFields: '*',
      numericFilters: [],
      facetFilters: []
    };

    let pos = this.map.getCenter();
    params.aroundLatLng = pos.lat + "," + pos.lng;
    let bounds = this.map.getBounds();
    params.aroundRadius = getMapRadius(bounds._ne, bounds._sw);
    params.aroundRadius += 50000;

    const _this = this;

    //get experiences found with experiences.hits
    this.algolia.search(params, function searchDone(err, experiences) {
      if (err) throw err;

      _this.updateMarkersOnMap(experiences.hits, _this.map); //updates markers on map
    });
  }

  updateMarkersOnMap(experiences, map){
    const _this = this;

    //Clears every current markers on map
    Object.values(this.markersPlaced).forEach(function (marker) {
      marker.remove();
    });

    //Regroup experiences by geoloc (lat, lng)
    let experiencesSortedByGeoLoc = groupBy(experiences, function (experience) {
      if (experience._geoloc.lat && experience._geoloc.lng) {
        return [experience._geoloc.lat, experience._geoloc.lng];
      }
    });

    if (this.mobileCardsContainer) {
      this.mobileCardsContainer.innerHTML = ``;
      this.mobileCardsContainer.classList.add("hidden");
      this.wrapper.addEventListener("click", function () {
        _this.mobileCardsContainer.innerHTML = ``;
        _this.mobileCardsContainer.classList.add("hidden");
      });
    }

    experiencesSortedByGeoLoc.forEach(function (experiences) {
      let marker = document.createElement('div');
      let experience = experiences[0];

      //Single experience at this geolocation
      if (experiences.length === 1) {
        marker.className = 'marker single-marker relative';

        if(experience.host.currency != _this.currencyInfos.iso)
          var amount = Math.ceil(experience.price_in_euros * _this.currencyInfos.rate);
        else
          var amount = experience.price_in_euros * _this.currencyInfos.rate;

        //format the experience price with the current currency
        let formattedPrice = new Intl.NumberFormat(_this.currencyInfos.locale, { style: 'currency', currency: _this.currencyInfos.iso, minimumFractionDigits: 0 }).format(amount);
        marker.textContent = formattedPrice;
        if (experience.booking_auto) { //if experience is in booking_auto mode, add the booking auto icon
          let icon = buildBookingAutoIcon();
          marker.appendChild(icon);
        }
      }

      //Multiple experiences at this geolocation (cluster)
      else if (experiences.length > 1) {
        let experiencesObjectIds = [];
        let atLeastOneBookingAuto = false;
        experiences.forEach(function (experience) {
          experiencesObjectIds.push(experience.objectID);

          if (experience.booking_auto) {
            atLeastOneBookingAuto = true;
          }
        });

        marker.className = 'marker cluster-markers ' + experiencesObjectIds.join(' ');
        marker.dataset.linkExperience = experience.objectID;
        marker.textContent = experiences.length;

        if (atLeastOneBookingAuto) { //if at least one experience in the cluster is in booking_auto mode, add the booking auto icon
          let icon = buildBookingAutoIcon();
          marker.appendChild(icon);
        }
      }

      marker.addEventListener('click', function () {
        //setTimeout is used to make sure this is done after map click event (which removes highlighted markers)
        setTimeout(function () {
          marker.classList.toggle("highlighted");
        }, 100);
      });

      // let onMobile = window.matchMedia("(max-width: 768px)").matches;
      // if (onMobile && _this.mobileCardsContainer) { //if user is on mobile
        marker.addEventListener("click", function () {
          setTimeout(function () {
            _this.mobileCardsContainer.innerHTML = buildMobilePopup(_this.wrapper, _this.currencyInfos, _this.mobileCardsContainer, experiences, '_blank', _this.utmParameters);
            _this.mobileCardsContainer.classList.remove("hidden");
          }, 100);
        });

        _this.markersPlaced[experience.objectID] = new maplibregl.Marker({element: marker})
          .setLngLat([experience._geoloc.lng, experience._geoloc.lat])
          .addTo(_this.map);
      // }
      // else { //if user is on tablet/desktop
        let popupOptions = {
          offset: 30,
          closeButton: false
        };
        let popup = new maplibregl.Popup(popupOptions).setHTML(buildPopupHTML(_this.wrapper, _this.currencyInfos, experiences, '_blank', _this.utmParameters));
        popup.on("open", function () {
          if (experiences.length > 1) {
            initializeGlider();
          }
        });

        _this.markersPlaced[experience.objectID] = new maplibregl.Marker({element: marker})
          .setLngLat([experience._geoloc.lng, experience._geoloc.lat])
          .setPopup(popup)
          .addTo(_this.map);
      // }
    });
  }
}

export default MapWidget;
